/* Glowen technology pages: scoped interactions and Shopify section lifecycle. */
(function (global) {
  'use strict';

  if (global.GlowenTechnologyPages) {
    global.GlowenTechnologyPages.mount(global.document);
    return;
  }

  var hostDocument = global.document;
  var selector = '[data-glowen-technologies]';
  var factories = new Map();
  var instances = new Map();
  var pendingScripts = new Map();

  function rootsWithin(container) {
    var roots = [];
    if (container && container.matches && container.matches(selector)) roots.push(container);
    if (container && container.querySelectorAll) roots.push.apply(roots, container.querySelectorAll(selector));
    return roots;
  }

  function createContext(root) {
    var active = true;
    var originalHTML = root.innerHTML;
    var originalClass = root.getAttribute('class');
    var originalStyle = root.getAttribute('style');
    var listeners = [];
    var observers = new Set();
    var timeouts = new Set();
    var intervals = new Set();
    var frames = new Set();
    var cleanupCallbacks = [];
    var proxyCache = new WeakMap();
    var proxyTargets = new WeakMap();
    var scopedDocument;
    var scopedWindow;
    var translationNode = root.querySelector('[data-gtech-translations]');
    var translations = Object.create(null);
    if (translationNode) {
      try { translations = JSON.parse(translationNode.textContent); }
      catch (error) { global.console.error('Glowen technology translations could not be parsed', error); }
    }
    function translate(key) {
      if (!translations || !Object.prototype.hasOwnProperty.call(translations, key) || typeof translations[key] !== 'string') {
        throw new Error('Missing Glowen technology translation: ' + key);
      }
      return translations[key];
    }

    function unwrap(value) { return value && proxyTargets.get(value) || value; }
    function invoke(callback, target, event) {
      if (!active) return;
      if (typeof callback === 'function') callback.call(wrap(target), event);
      else if (callback && callback.handleEvent) callback.handleEvent(event);
    }
    function capture(options) { return typeof options === 'boolean' ? options : !!(options && options.capture); }

    function addListener(target, type, callback, options) {
      if (!active || !callback) return;
      if (target === hostDocument && type === 'DOMContentLoaded') {
        // The section already exists when mounted, including inside the theme editor.
        var ready = { target: target, type: type, callback: callback, capture: capture(options), cancelled: false };
        listeners.push(ready);
        Promise.resolve().then(function () {
          if (!ready.cancelled) invoke(callback, target, new global.Event('DOMContentLoaded'));
        });
        return;
      }
      if (listeners.some(function (item) { return item.target === target && item.type === type && item.callback === callback && item.capture === capture(options); })) return;
      var record = { target: target, type: type, callback: callback, capture: capture(options) };
      record.handler = function (event) {
        if (options && options.once) listeners = listeners.filter(function (item) { return item !== record; });
        invoke(callback, target, event);
      };
      listeners.push(record);
      target.addEventListener(type, record.handler, options);
    }

    function removeListener(target, type, callback, options) {
      listeners = listeners.filter(function (item) {
        if (item.target !== target || item.type !== type || item.callback !== callback || item.capture !== capture(options)) return true;
        item.cancelled = true;
        if (item.handler) target.removeEventListener(type, item.handler, item.capture);
        return false;
      });
    }

    function nodeCollection(collection) {
      var result = Array.from(collection, wrap);
      result.item = function (index) { return result[index] || null; };
      return result;
    }

    function wrap(value) {
      if (!value || (typeof value !== 'object' && typeof value !== 'function')) return value;
      if (value === hostDocument) return scopedDocument;
      if (value === global) return scopedWindow;
      if (proxyTargets.has(value)) return value;
      if ((global.NodeList && value instanceof global.NodeList) || (global.HTMLCollection && value instanceof global.HTMLCollection)) return nodeCollection(value);
      if (!value.nodeType && !(typeof value.addEventListener === 'function' && typeof value.matches === 'boolean')) return value;
      if (proxyCache.has(value)) return proxyCache.get(value);
      var proxy = new Proxy(value, {
        get: function (target, key) {
          if (key === 'addEventListener') return function (type, callback, options) { addListener(target, type, callback, options); };
          if (key === 'removeEventListener') return function (type, callback, options) { removeListener(target, type, callback, options); };
          if (key === 'addListener' && !target.nodeType) return function (callback) { addListener(target, 'change', callback); };
          if (key === 'removeListener' && !target.nodeType) return function (callback) { removeListener(target, 'change', callback); };
          var result = Reflect.get(target, key, target);
          if (typeof result === 'function') return function () {
            return wrap(result.apply(target, Array.from(arguments, unwrap)));
          };
          return wrap(result);
        },
        set: function (target, key, value) { return Reflect.set(target, key, unwrap(value), target); }
      });
      proxyCache.set(value, proxy);
      proxyTargets.set(proxy, value);
      return proxy;
    }

    function trackedObserver(name) {
      var NativeObserver = global[name];
      if (!NativeObserver) return undefined;
      return function (callback, options) {
        var settings = options ? Object.assign({}, options) : options;
        if (settings && settings.root) settings.root = unwrap(settings.root);
        var observerProxy;
        var nativeObserver = new NativeObserver(function (entries) {
          if (!active) return;
          // Match the same element proxies returned by scoped document queries.
          var scopedEntries = entries.map(function (entry) {
            return new Proxy(entry, { get: function (target, key) { return key === 'target' ? wrap(target.target) : Reflect.get(target, key, target); } });
          });
          callback(scopedEntries, observerProxy);
        }, settings);
        observers.add(nativeObserver);
        observerProxy = new Proxy(nativeObserver, { get: function (target, key) {
          var value = Reflect.get(target, key, target);
          return typeof value === 'function' ? function () { return value.apply(target, Array.from(arguments, unwrap)); } : value;
        } });
        return observerProxy;
      };
    }

    function requestFrame(callback) {
      if (!active) return 0;
      var id = global.requestAnimationFrame(function (time) { frames.delete(id); if (active) callback(time); });
      frames.add(id);
      return id;
    }
    function cancelFrame(id) { frames.delete(id); global.cancelAnimationFrame(id); }
    function setTrackedTimeout(callback, delay) {
      if (!active) return 0;
      var args = Array.prototype.slice.call(arguments, 2);
      var id = global.setTimeout(function () { timeouts.delete(id); if (active) callback.apply(scopedWindow, args); }, delay);
      timeouts.add(id);
      return id;
    }
    function clearTrackedTimeout(id) { timeouts.delete(id); global.clearTimeout(id); }
    function setTrackedInterval(callback, delay) {
      if (!active) return 0;
      var args = Array.prototype.slice.call(arguments, 2);
      var id = global.setInterval(function () { if (active) callback.apply(scopedWindow, args); }, delay);
      intervals.add(id);
      return id;
    }
    function clearTrackedInterval(id) { intervals.delete(id); global.clearInterval(id); }

    var api = {
      t: translate,
      root: wrap(root),
      IntersectionObserver: trackedObserver('IntersectionObserver'),
      ResizeObserver: trackedObserver('ResizeObserver'),
      requestAnimationFrame: requestFrame,
      cancelAnimationFrame: cancelFrame,
      setTimeout: setTrackedTimeout,
      clearTimeout: clearTrackedTimeout,
      setInterval: setTrackedInterval,
      clearInterval: clearTrackedInterval,
      onCleanup: function (callback) { cleanupCallbacks.push(callback); }
    };

    scopedDocument = new Proxy(hostDocument, {
      get: function (target, key) {
        if (key === 'body' || key === 'documentElement') return api.root;
        if (key === 'defaultView') return scopedWindow;
        if (key === 'activeElement') return root.contains(target.activeElement) ? wrap(target.activeElement) : null;
        if (key === 'getElementById') return function (id) {
          if (root.id === id) return api.root;
          var element = root.querySelector('[id="' + String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]');
          return wrap(element);
        };
        if (key === 'querySelector') return function (query) { return wrap(root.querySelector(query)); };
        if (key === 'querySelectorAll') return function (query) { return nodeCollection(root.querySelectorAll(query)); };
        if (key === 'getElementsByClassName' || key === 'getElementsByTagName') return function (query) { return nodeCollection(root[key](query)); };
        if (key === 'addEventListener') return function (type, callback, options) { addListener(target, type, callback, options); };
        if (key === 'removeEventListener') return function (type, callback, options) { removeListener(target, type, callback, options); };
        var value = Reflect.get(target, key, target);
        return typeof value === 'function' ? function () { return wrap(value.apply(target, Array.from(arguments, unwrap))); } : wrap(value);
      }
    });
    scopedWindow = new Proxy(global, {
      get: function (target, key) {
        if (key === 'document') return scopedDocument;
        if (key === 'window' || key === 'self') return scopedWindow;
        if (Object.prototype.hasOwnProperty.call(api, key)) return api[key];
        if (key === 'addEventListener') return function (type, callback, options) { addListener(target, type, callback, options); };
        if (key === 'removeEventListener') return function (type, callback, options) { removeListener(target, type, callback, options); };
        if (key === 'matchMedia') return function (query) { return wrap(target.matchMedia(query)); };
        var value = Reflect.get(target, key, target);
        return typeof value === 'function' ? value.bind(target) : value;
      },
      has: function (target, key) { return key in api ? api[key] !== undefined : key in target; }
    });
    api.document = scopedDocument;
    api.window = scopedWindow;

    // A few SVG callouts occupy fixed diagram cells. Fit only translated labels
    // that exceed their cell, measuring again from the original font each time.
    var labelFontStyles = new WeakMap();
    var labelFitFrame = 0;
    function fitDiagramLabels() {
      if (!active) return;
      root.querySelectorAll('svg text[data-gtech-max-label-width]').forEach(function (label) {
        var maxWidth = Number(label.getAttribute('data-gtech-max-label-width'));
        if (!Number.isFinite(maxWidth) || maxWidth <= 0 || typeof label.getComputedTextLength !== 'function') return;
        if (!labelFontStyles.has(label)) {
          labelFontStyles.set(label, {
            value: label.style.getPropertyValue('font-size'),
            priority: label.style.getPropertyPriority('font-size')
          });
        }
        var original = labelFontStyles.get(label);
        if (original.value) label.style.setProperty('font-size', original.value, original.priority);
        else label.style.removeProperty('font-size');
        var width;
        try { width = label.getComputedTextLength(); }
        catch (error) { return; }
        if (!Number.isFinite(width) || width <= maxWidth) return;
        var fontSize = parseFloat(global.getComputedStyle(label).fontSize);
        if (!Number.isFinite(fontSize) || fontSize <= 0) return;
        label.style.setProperty('font-size', (fontSize * maxWidth / width) + 'px', original.priority);
      });
    }
    function scheduleDiagramLabels() {
      if (!active || labelFitFrame) return;
      labelFitFrame = api.requestAnimationFrame(function () {
        labelFitFrame = 0;
        fitDiagramLabels();
      });
    }
    addListener(global, 'resize', scheduleDiagramLabels, { passive: true });
    addListener(root, 'click', scheduleDiagramLabels, { passive: true });
    if (hostDocument.fonts && hostDocument.fonts.ready) {
      hostDocument.fonts.ready.then(function () { if (active) scheduleDiagramLabels(); });
    }
    scheduleDiagramLabels();

    // The real Glowen theme has a sticky #header-group. Keep in-page navigation
    // beneath it without changing the theme's header, body, or root CSS variables.
    var header = hostDocument.getElementById('header-group');
    var navigation = root.querySelector('.gtech-contents');
    function measureOffsets() {
      if (!active) return;
      var headerHeight = header ? Math.max(0, header.getBoundingClientRect().height) : 0;
      var navHeight = navigation ? Math.max(0, navigation.getBoundingClientRect().height) : 0;
      root.style.setProperty('--gtech-header-offset', headerHeight + 'px');
      root.style.setProperty('--gtech-nav-height', navHeight + 'px');
      root.dataset.scrollThreshold = String(headerHeight + navHeight + 24);
    }
    addListener(global, 'resize', measureOffsets, { passive: true });
    if (global.ResizeObserver) {
      var layoutObserver = new global.ResizeObserver(measureOffsets);
      observers.add(layoutObserver);
      if (header) layoutObserver.observe(header);
      if (navigation) layoutObserver.observe(navigation);
    }
    measureOffsets();

    api.destroy = function () {
      if (!active) return;
      active = false;
      listeners.forEach(function (item) { item.cancelled = true; if (item.handler) item.target.removeEventListener(item.type, item.handler, item.capture); });
      listeners = [];
      observers.forEach(function (observer) { observer.disconnect(); });
      observers.clear();
      frames.forEach(function (id) { global.cancelAnimationFrame(id); });
      timeouts.forEach(function (id) { global.clearTimeout(id); });
      intervals.forEach(function (id) { global.clearInterval(id); });
      frames.clear(); timeouts.clear(); intervals.clear();
      cleanupCallbacks.forEach(function (callback) { try { callback(); } catch (error) { global.console.error('Glowen technology cleanup failed', error); } });
      // Shopify normally replaces this node. Restoring it also makes explicit
      // destroy/remount safe: no duplicate figure wrappers or stale ready flags.
      root.innerHTML = originalHTML;
      if (originalClass === null) root.removeAttribute('class'); else root.setAttribute('class', originalClass);
      if (originalStyle === null) root.removeAttribute('style'); else root.setAttribute('style', originalStyle);
      delete root.dataset.scrollThreshold;
      delete root.dataset.technologyReady;
      delete root.dataset.technologyErrors;
    };
    return api;
  }

  function loadModel(root) {
    var model = root.dataset.model;
    if (pendingScripts.has(model)) return;
    var section = root.closest('.shopify-section') || root.parentElement;
    var supplied = section && Array.from(section.querySelectorAll('script[src]')).find(function (script) {
      return script.getAttribute('src').split('?')[0].endsWith('/glowen-technologies-' + model + '.js');
    });
    var source = root.dataset.script || supplied && supplied.getAttribute('src');
    if (!source) return;
    var script = hostDocument.createElement('script');
    script.src = source;
    script.async = true;
    script.dataset.glowenTechnologyLoader = model;
    pendingScripts.set(model, script);
    script.addEventListener('error', function () {
      pendingScripts.delete(model);
      root.dataset.technologyErrors = 'asset-load';
      script.remove();
      global.console.error('Glowen technology model could not be loaded:', model);
    }, { once: true });
    script.addEventListener('load', function () { script.remove(); }, { once: true });
    hostDocument.head.appendChild(script);
  }

  function mount(container) {
    rootsWithin(container || hostDocument).forEach(function (root) {
      if (instances.has(root)) return;
      var factory = factories.get(root.dataset.model);
      if (!factory) { loadModel(root); return; }
      var context = createContext(root);
      instances.set(root, context);
      try {
        var cleanup = factory(context);
        if (typeof cleanup === 'function') context.onCleanup(cleanup);
        root.dataset.technologyReady = 'true';
      } catch (error) {
        root.dataset.technologyErrors = 'initialization';
        global.console.error('Glowen technology page initialization failed:', root.dataset.model, error);
      }
    });
  }

  function destroy(container) {
    rootsWithin(container).forEach(function (root) {
      var context = instances.get(root);
      if (context) { context.destroy(); instances.delete(root); }
    });
  }

  var runtime = {
    version: '2.1.0',
    register: function (model, factory) {
      if (!factories.has(model)) factories.set(model, factory);
      mount(hostDocument);
    },
    mount: mount,
    destroy: destroy
  };
  global.GlowenTechnologyPages = runtime;
  hostDocument.addEventListener('shopify:section:load', function (event) { mount(event.target); });
  hostDocument.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });
  hostDocument.addEventListener('shopify:section:reorder', function () { global.dispatchEvent(new global.Event('resize')); });
  var queued = global.GlowenTechnologyQueue || [];
  global.GlowenTechnologyQueue = [];
  queued.forEach(function (install) { install(runtime); });
  if (hostDocument.readyState === 'loading') hostDocument.addEventListener('DOMContentLoaded', function () { mount(hostDocument); }, { once: true });
  else mount(hostDocument);
})(window);
